import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import Header from './components/Header3';
import Footer from './components/Footer3';

const Template3Shop = () => {
    const navigate = useNavigate();
    const [activeFilter, setActiveFilter] = useState('All');
    const [sortBy, setSortBy] = useState('featured');
    const [currentPage, setCurrentPage] = useState(1);
    const [wishlist, setWishlist] = useState({});
    const [hoveredCard, setHoveredCard] = useState(null);

    const categories = ['All', 'Lehengas', 'Sarees', 'Sherwanis', 'Anarkalis', 'Indo-Western'];

    const sortOptions = [
        { value: 'featured', label: 'Featured' },
        { value: 'price-low', label: 'Price: Low to High' },
        { value: 'price-high', label: 'Price: High to Low' },
        { value: 'newest', label: 'Newest First' },
    ];

    const products = [
        { id: 1, name: 'Royal Banarasi Lehenga', category: 'Lehengas', price: 49999, badge: 'NEW', image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 2, name: 'Silk Chanderi Saree', category: 'Sarees', price: 18500, image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 3, name: 'Gold Zardozi Sherwani', category: 'Sherwanis', price: 32000, badge: 'BESTSELLER', image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 4, name: 'Maroon Velvet Anarkali', category: 'Anarkalis', price: 15999, image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 5, name: 'Heritage Patola Dupatta', category: 'Indo-Western', price: 12499, image: 'https://images.pexels.com/photos/6712360/pexels-photo-6712360.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 6, name: 'Ivory Bridal Lehenga', category: 'Lehengas', price: 45000, badge: 'NEW', image: 'https://images.pexels.com/photos/7275385/pexels-photo-7275385.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 7, name: 'Wine Silk Kurta Set', category: 'Indo-Western', price: 5999, image: 'https://images.pexels.com/photos/8953474/pexels-photo-8953474.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 8, name: 'Festive Organza Saree', category: 'Sarees', price: 21500, image: 'https://images.pexels.com/photos/2955375/pexels-photo-2955375.jpeg?auto=compress&cs=tinysrgb&w=800' },
    ];

    const filteredProducts = activeFilter === 'All' ? products : products.filter(p => p.category === activeFilter);

    const sortedProducts = [...filteredProducts].sort((a, b) => {
        if (sortBy === 'price-low') return a.price - b.price;
        if (sortBy === 'price-high') return b.price - a.price;
        return 0;
    });

    const toggleWishlist = (e, productId) => {
        e.stopPropagation();
        setWishlist(prev => ({ ...prev, [productId]: !prev[productId] }));
    };

    const totalPages = 3;

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Lato:wght@300;400;700&display=swap');
            `}</style>

            <div style={{ fontFamily: "'Lato', sans-serif" }} className="min-h-screen bg-[#FFFDF7]">
                <Header />

                <main className="pt-24 pb-16">
                    {/* Page Header */}
                    <div className="text-center py-10 lg:py-16 px-4" style={{ borderBottom: '1px solid #E8E0D8' }}>
                        <p className="text-xs font-bold tracking-[0.3em] uppercase mb-3" style={{ color: '#B8860B' }}>
                            Curated for you
                        </p>
                        <h1
                            style={{ fontFamily: "'Playfair Display', serif" }}
                            className="text-3xl lg:text-5xl font-bold text-[#722F37] tracking-wide"
                        >
                            Our Collection
                        </h1>
                        <p className="text-sm text-[#6B7280] mt-3 max-w-md mx-auto">
                            Discover handcrafted luxury pieces that celebrate the art of Indian couture
                        </p>
                    </div>

                    <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-8">
                        {/* Filters */}
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
                            {/* Category Tabs */}
                            <div className="flex flex-wrap gap-2">
                                {categories.map(cat => (
                                    <button
                                        key={cat}
                                        onClick={() => setActiveFilter(cat)}
                                        className="px-4 py-2 text-xs font-bold tracking-wider uppercase transition-all duration-300"
                                        style={{
                                            backgroundColor: activeFilter === cat ? '#722F37' : 'transparent',
                                            color: activeFilter === cat ? '#FFFFFF' : '#722F37',
                                            border: `1px solid ${activeFilter === cat ? '#722F37' : '#E8E0D8'}`,
                                        }}
                                    >
                                        {cat}
                                    </button>
                                ))}
                            </div>

                            {/* Sort */}
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="px-4 py-2 text-sm outline-none cursor-pointer"
                                style={{
                                    backgroundColor: '#FFFFFF',
                                    border: '1px solid #E8E0D8',
                                    color: '#1A1A1A',
                                    fontFamily: "'Lato', sans-serif",
                                }}
                            >
                                {sortOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Product Grid */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6">
                            {sortedProducts.map(product => (
                                <div
                                    key={product.id}
                                    onClick={() => navigate(`/template3/product/${product.id}`)}
                                    onMouseEnter={() => setHoveredCard(product.id)}
                                    onMouseLeave={() => setHoveredCard(null)}
                                    className="cursor-pointer group"
                                    style={{
                                        backgroundColor: '#FFFFFF',
                                        border: `1px solid ${hoveredCard === product.id ? '#722F37' : '#E8E0D8'}`,
                                        transition: 'all 0.3s ease',
                                        transform: hoveredCard === product.id ? 'translateY(-4px)' : 'translateY(0)',
                                        boxShadow: hoveredCard === product.id ? '0 8px 30px rgba(114,47,55,0.12)' : 'none',
                                    }}
                                >
                                    <div className="relative aspect-[3/4] overflow-hidden">
                                        <img
                                            src={product.image}
                                            alt={product.name}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        />
                                        {product.badge && (
                                            <span
                                                className="absolute top-3 left-3 px-3 py-1 text-[10px] font-bold tracking-wider uppercase"
                                                style={{ backgroundColor: '#722F37', color: '#FFFFFF' }}
                                            >
                                                {product.badge}
                                            </span>
                                        )}
                                        <button
                                            onClick={(e) => toggleWishlist(e, product.id)}
                                            className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-colors"
                                            style={{ backgroundColor: 'rgba(255,253,247,0.9)' }}
                                        >
                                            <Heart
                                                className="w-4 h-4"
                                                style={{
                                                    color: wishlist[product.id] ? '#722F37' : '#6B7280',
                                                    fill: wishlist[product.id] ? '#722F37' : 'none',
                                                }}
                                            />
                                        </button>
                                    </div>
                                    <div className="p-3 lg:p-4">
                                        <h3 className="text-xs lg:text-sm font-normal line-clamp-1 mb-1 lg:mb-2" style={{ color: '#1A1A1A' }}>
                                            {product.name}
                                        </h3>
                                        <p className="text-sm lg:text-base font-bold" style={{ color: '#B8860B' }}>
                                            {`\u20B9${product.price.toLocaleString('en-IN')}`}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        <div className="flex items-center justify-center gap-2 mt-12">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="w-10 h-10 flex items-center justify-center transition-colors disabled:opacity-30"
                                style={{ border: '1px solid #E8E0D8', color: '#722F37' }}
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            {[1, 2, 3].map(page => (
                                <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    className="w-10 h-10 flex items-center justify-center text-sm font-bold transition-all"
                                    style={{
                                        backgroundColor: currentPage === page ? '#722F37' : 'transparent',
                                        color: currentPage === page ? '#FFFFFF' : '#722F37',
                                        border: `1px solid ${currentPage === page ? '#722F37' : '#E8E0D8'}`,
                                    }}
                                >
                                    {page}
                                </button>
                            ))}
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="w-10 h-10 flex items-center justify-center transition-colors disabled:opacity-30"
                                style={{ border: '1px solid #E8E0D8', color: '#722F37' }}
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </main>

                <Footer />
            </div>
        </>
    );
};

export default Template3Shop;
